// subscription.service.ts
import api from './api';
import axios from 'axios';

// Define supported currencies for the application
export const SUPPORTED_CURRENCIES = ['NGN', 'USD', 'GHS', 'KES', 'ZAR', 'XOF'];

// Define currency symbols for display
export const CURRENCY_SYMBOLS = {
  USD: '$',
  NGN: '₦',
  GHS: 'GH₵',
  KES: 'KSh',
  ZAR: 'R',
  XOF: 'CFA'
};

/**
 * Service for handling subscription-related API calls
 */
const SubscriptionService = {
  /**
   * Get the current user's subscription status
   * @returns Promise with subscription status
   */
  getStatus: () => {
    return api.get('/subscriptions/status');
  },

  /**
   * Upgrade to a premium plan
   * @param planType - The plan type (premium or ultimate)
   * @param planDuration - The billing period (monthly or yearly)
   * @param currency - Optional currency to use for payment (NGN, USD, etc.)
   * @returns Promise with payment initialization data
   */
  upgrade: (
    planType: 'basic' | 'premium' | 'ultimate',
    planDuration: 'monthly' | 'yearly',
    currency?: string
  ) => {
    const payload: {
      planType: 'basic' | 'premium' | 'ultimate';
      planDuration: 'monthly' | 'yearly';
      currency?: string;
    } = {
      planType,
      planDuration
    };
    
    // Only add currency if it's provided
    if (currency) {
      payload.currency = currency;
    }
    
    return api.post('/subscriptions/upgrade', payload);
  },

  /**
   * Cancel the current subscription
   * @returns Promise with cancellation result
   */
  cancel: () => {
    return api.post('/subscriptions/cancel');
  },

  /**
   * Verify a payment using the Paystack reference (authenticated)
   * @param reference - The payment reference from Paystack
   * @returns Promise with verification result
   */
  verify: (reference: string) => {
    return api.post('/subscriptions/verify', { reference });
  },

/**
   * Verify a subscription payment for non-authenticated users
   * @param reference - The payment reference from Paystack
   * @returns Promise with verification result
   */
verifyPublic: (reference: string) => {
    const apiUrl = process.env.REACT_APP_API_URL || 'https://wedding-backend-p3y8.onrender.com';
    return axios.post(`${apiUrl}/subscriptions/public-verify`, { reference }, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  },

  /**
   * Get plan details by plan code
   * @param planCode - The Paystack plan code
   * @returns Promise with plan details
   */
  getPlanDetails: (planCode: string) => {
    return api.get(`/subscriptions/plan/${planCode}`);
  },

  /**
   * Verify a subscription payment
   * @param reference - The payment reference from Paystack
   * @returns Promise with verification result
   */
  verifySubscription: (reference: string) => {
    return api.post('/subscriptions/verify-subscription', { reference });
  },

  /**
   * Get pricing information for all plans or specific currency
   * @param currency - Optional currency code
   * @returns Promise with pricing information
   */
  getPricing: (currency?: string) => {
    const url = currency ? `/subscriptions/pricing?currency=${currency}` : '/subscriptions/pricing';
    return api.get(url);
  },

  /**
   * Update user's preferred currency
   * @param currency - Currency code to set as preferred
   * @returns Promise with update result
   */
  updateCurrency: (currency: string) => {
    return api.post('/subscriptions/currency', { currency });
  },

  /**
   * Get supported currencies
   * @returns Promise with list of supported currencies
   */
  getSupportedCurrencies: () => {
    return api.get('/subscriptions/currencies');
  },

  /**
   * Get currency symbols for display
   * @returns Promise with currency symbols
   */
  getCurrencySymbols: () => {
    return api.get('/subscriptions/currency-symbols');
  },
  
  /**
   * Format price with currency symbol
   * @param price - The numeric price value
   * @param currency - The currency code
   * @returns Formatted price string with currency symbol
   */
  formatPrice: (price: number, currency: string = 'NGN'): string => {
    const symbol = CURRENCY_SYMBOLS[currency as keyof typeof CURRENCY_SYMBOLS] || '';
    
    // Special formatting for certain currencies
    if (currency === 'USD') {
      return `${symbol}${price.toFixed(2)}`;
    }
    
    // For currencies with larger values, no decimal places
    return `${symbol}${Math.round(price).toLocaleString()}`;
  },
  
  /**
   * Calculate savings percentage between monthly and yearly plans
   * @param monthlyPrice - The monthly price
   * @param yearlyPrice - The yearly price
   * @returns Percentage saved with yearly plan
   */
  calculateSavings: (monthlyPrice: number, yearlyPrice: number): number => {
    const annualMonthlyPrice = monthlyPrice * 12;
    return Math.round(((annualMonthlyPrice - yearlyPrice) / annualMonthlyPrice) * 100);
  }
};

export default SubscriptionService;